import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ncoder/ui';
import { InteractiveTest } from '../../molecules/testing/InteractiveTest';
import { TestScenarios } from '../../molecules/testing/TestScenarios';
import { TestResultsList } from '../../molecules/testing/TestResultsList';
import { PerformanceMetrics } from '../../molecules/testing/PerformanceMetrics';
import { TestStatusPanel } from '../../molecules/testing/TestStatusPanel';
import { QuickActionsPanel } from '../../molecules/testing/QuickActionsPanel';
import { TestCategoriesPanel } from '../../molecules/testing/TestCategoriesPanel';

interface TestResult {
  id: number;
  scenario: string;
  status: 'passed' | 'failed';
  response: string;
  score: number;
  timestamp: string;
}

interface TestCategory {
  id: string;
  label: string;
  status: 'passed' | 'needs-work';
}

interface AgentTestingContentProps {
  // Interactive test
  testMessage: string;
  isRunning: boolean;
  onMessageChange: (value: string) => void;
  onSendTest: () => void;
  
  // Scenarios
  onScenarioClick?: (scenario: string) => void;
  
  // Results
  testResults: TestResult[];
  
  // Performance
  successRate: string;
  avgResponseTime: string;
  userSatisfaction: string;
  testsRun: string;
  
  // Status
  testsPassed: number;
  testsFailed: number;
  statusSuccessRate: string;
  
  // Categories
  categories: TestCategory[];
  
  // Quick actions
  onRunQuickTest?: () => void;
  onViewAnalytics?: () => void;
  onCustomTestSuite?: () => void;
}

export const AgentTestingContent: React.FC<AgentTestingContentProps> = ({
  testMessage,
  isRunning,
  onMessageChange,
  onSendTest,
  onScenarioClick,
  testResults,
  successRate,
  avgResponseTime,
  userSatisfaction,
  testsRun,
  testsPassed,
  testsFailed,
  statusSuccessRate,
  categories,
  onRunQuickTest,
  onViewAnalytics,
  onCustomTestSuite,
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Content - 2/3 width */}
      <div className="lg:col-span-2 space-y-6">
        <Tabs defaultValue="interactive" className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
            <TabsTrigger value="interactive" className="text-xs sm:text-sm">Interactive Test</TabsTrigger>
            <TabsTrigger value="scenarios" className="text-xs sm:text-sm">Test Scenarios</TabsTrigger>
            <TabsTrigger value="results" className="text-xs sm:text-sm">Test Results</TabsTrigger>
            <TabsTrigger value="performance" className="text-xs sm:text-sm">Performance</TabsTrigger>
          </TabsList>

          <TabsContent value="interactive" className="space-y-4">
            <InteractiveTest
              testMessage={testMessage}
              isRunning={isRunning}
              onMessageChange={onMessageChange}
              onSendTest={onSendTest}
            />
          </TabsContent>

          <TabsContent value="scenarios" className="space-y-4">
            <TestScenarios onScenarioClick={onScenarioClick} />
          </TabsContent>

          <TabsContent value="results" className="space-y-4">
            <TestResultsList results={testResults} />
          </TabsContent>

          <TabsContent value="performance" className="space-y-4">
            <PerformanceMetrics
              successRate={successRate}
              avgResponseTime={avgResponseTime}
              userSatisfaction={userSatisfaction}
              testsRun={testsRun}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Sidebar - 1/3 width */}
      <div className="space-y-6">
        <TestStatusPanel
          testsPassed={testsPassed}
          testsFailed={testsFailed}
          successRate={statusSuccessRate}
        />

        <QuickActionsPanel
          onRunQuickTest={onRunQuickTest}
          onViewAnalytics={onViewAnalytics}
          onCustomTestSuite={onCustomTestSuite}
        />

        <TestCategoriesPanel categories={categories} />
      </div>
    </div>
  );
};
